// Página explicativa de las reglas y funcionamiento del pozo de apuestas
import React from 'react'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { 
  Trophy, HelpCircle, Coins, ShieldAlert, 
  ArrowRight, Users, CheckCircle, Percent
} from 'lucide-react'
import client from '../api/client'
import Card from '../components/ui/Card'

export default function Rules() {
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await client.get('/settings')
      return response.data?.datos || {}
    },
    staleTime: 5 * 60 * 1000,
  })

  const nombreSistema = settings?.nombre_sistema || 'SubiGol'
  const comision = settings?.comision_apuestas || '10'
  const multiplicador = Number(settings?.multiplicador_goles_exacto || '3')
  const dividendoMinimo = settings?.dividendo_minimo_ganador || '1.05'

  const comisionPct = parseFloat(comision)
  const totalRecaudado = 620
  const pozoNeto = totalRecaudado * (1 - comisionPct / 100)
  
  const anaTickets = 100 * multiplicador
  const brunoTickets = 50 * multiplicador
  const claraTickets = 100
  const diegoTickets = 50
  
  const totalTickets = anaTickets + brunoTickets + claraTickets + diegoTickets
  const valorTicket = pozoNeto / totalTickets

  const anaPremio = anaTickets * valorTicket
  const brunoPremio = brunoTickets * valorTicket
  const claraPremio = claraTickets * valorTicket
  const diegoPremio = diegoTickets * valorTicket

  return (
    <div className="py-8 px-4 sm:py-12 sm:px-6 lg:px-8 max-w-5xl mx-auto space-y-12 animate-fade-in font-body">
      {/* Encabezado */}
      <div className="text-center space-y-3">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="inline-flex p-3 rounded-2xl bg-cyan/10 border border-cyan/20 text-cyan mb-2"
        >
          <HelpCircle className="w-8 h-8" />
        </motion.div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold text-text-primary">
          Reglas de Juego y Apuestas
        </h1>
        <p className="text-text-secondary max-w-2xl mx-auto text-sm sm:text-base">
          En {nombreSistema} jugamos con un sistema de <strong className="font-semibold text-text-primary">Pozo Acumulado (Pari-mutuel)</strong>. Aquí te explicamos cómo competir, ganar y multiplicar tus créditos.
        </p>
      </div>

      {/* Grid de Reglas Básicas */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="p-6 border-l-4 border-l-cyan space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan/10 text-cyan">
              <Coins className="w-5 h-5" />
            </div>
            <h3 className="font-display font-bold text-lg text-text-primary">1. Pozo Común</h3>
          </div>
          <p className="text-xs sm:text-sm text-text-secondary leading-relaxed">
            Todas las apuestas colocadas para un partido se juntan en un <strong className="font-semibold text-text-primary">pozo común</strong>. La plataforma retiene una comisión administrativa del <strong className="font-semibold text-text-primary">{comision}%</strong> y el resto del pozo se divide entre los usuarios que hayan acertado el resultado básico (Local, Empate o Visitante).
          </p>
        </Card>

        <Card className="p-6 border-l-4 border-l-warning space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-warning/10 text-warning">
              <Trophy className="w-5 h-5" />
            </div>
            <h3 className="font-display font-bold text-lg text-text-primary">2. Bono Goles (x{multiplicador})</h3>
          </div>
          <p className="text-xs sm:text-sm text-text-secondary leading-relaxed">
            Si aciertas el resultado básico y además <strong className="font-semibold text-text-primary">aciertas el marcador exacto del partido</strong> (los goles de ambos equipos), tu apuesta cuenta por <strong className="font-semibold text-text-primary">{multiplicador} participaciones</strong> del pozo en lugar de 1, obteniendo un premio multiplicado frente al resto.
          </p>
        </Card>

        <Card className="p-6 border-l-4 border-l-success space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-success/10 text-success">
              <Percent className="w-5 h-5" />
            </div>
            <h3 className="font-display font-bold text-lg text-text-primary">3. Dividendo Mínimo</h3>
          </div>
          <p className="text-xs sm:text-sm text-text-secondary leading-relaxed">
            Para proteger tu juego en partidos de alta certeza, garantizamos un retorno mínimo de <strong className="font-semibold text-text-primary">{dividendoMinimo}x</strong> tu apuesta si ganas. Reduciremos dinámicamente nuestra comisión a 0% o inyectaremos un subsidio para asegurar tu dividendo.
          </p>
        </Card>
      </div>

      {/* Ejemplo de 10 jugadores */}
      <Card className="p-6 sm:p-8 space-y-6 bg-surface-card/60 backdrop-blur-md">
        <div className="border-b border-border-subtle pb-4">
          <h2 className="font-display font-bold text-xl sm:text-2xl text-cyan flex items-center gap-2">
            <Users className="w-6 h-6" /> Ejemplo Práctico de Reparto (10 Participantes)
          </h2>
          <p className="text-xs sm:text-sm text-text-muted mt-1">
            Simulación matemática de un partido hipotético para entender cómo se calcula cada premio.
          </p>
        </div>

        {/* Premisa */}
        <div className="p-4 rounded-xl bg-slate-50 dark:bg-navy-dark/40 border border-border-subtle/50 text-sm space-y-2">
          <p className="font-semibold text-text-primary">Partido: Argentina vs Francia</p>
          <p className="text-text-secondary">
            Resultado Real: <strong className="font-semibold text-text-primary">Empate 2 - 2</strong> (Opción Ganadora: <strong className="font-semibold text-text-primary">Empate</strong>, Marcador exacto: <strong className="font-semibold text-text-primary">2 - 2</strong>).
          </p>
          <p className="text-text-secondary">
            Comisión de la plataforma: <strong className="font-semibold text-text-primary">{comision}%</strong>.
          </p>
        </div>

        {/* Tabla de Jugadores */}
        <div className="overflow-x-auto border border-border-subtle rounded-xl">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-100 dark:bg-navy-dark text-xs uppercase tracking-wider text-text-muted border-b border-border-subtle">
              <tr>
                <th className="px-4 py-3">Usuario</th>
                <th className="px-4 py-3">Elección</th>
                <th className="px-4 py-3 text-center">Marcador</th>
                <th className="px-4 py-3 text-right">Apostado</th>
                <th className="px-4 py-3 text-center">Bono x{multiplicador}</th>
                <th className="px-4 py-3 text-center">Tickets (Part.)</th>
                <th className="px-4 py-3 text-right text-cyan">Premio Final</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {/* Ganadores con Bono */}
              <tr className="bg-success/5 hover:bg-success/10 transition-colors">
                <td className="px-4 py-3 font-semibold text-success">Ana</td>
                <td className="px-4 py-3">Empate</td>
                <td className="px-4 py-3 text-center font-mono">2 - 2</td>
                <td className="px-4 py-3 text-right font-mono">100.00 Bs</td>
                <td className="px-4 py-3 text-center text-success">Sí ✅</td>
                <td className="px-4 py-3 text-center font-mono font-bold">{anaTickets}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-success">{anaPremio.toFixed(2)} Bs</td>
              </tr>
              <tr className="bg-success/5 hover:bg-success/10 transition-colors">
                <td className="px-4 py-3 font-semibold text-success">Bruno</td>
                <td className="px-4 py-3">Empate</td>
                <td className="px-4 py-3 text-center font-mono">2 - 2</td>
                <td className="px-4 py-3 text-right font-mono">50.00 Bs</td>
                <td className="px-4 py-3 text-center text-success">Sí ✅</td>
                <td className="px-4 py-3 text-center font-mono font-bold">{brunoTickets}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-success">{brunoPremio.toFixed(2)} Bs</td>
              </tr>
              {/* Ganadores sin Bono */}
              <tr className="hover:bg-slate-100 dark:hover:bg-navy-dark/20 transition-colors">
                <td className="px-4 py-3 font-medium text-text-primary">Clara</td>
                <td className="px-4 py-3">Empate</td>
                <td className="px-4 py-3 text-center font-mono">0 - 0</td>
                <td className="px-4 py-3 text-right font-mono">100.00 Bs</td>
                <td className="px-4 py-3 text-center text-text-muted">No ❌</td>
                <td className="px-4 py-3 text-center font-mono">{claraTickets}</td>
                <td className="px-4 py-3 text-right font-mono text-text-secondary">{claraPremio.toFixed(2)} Bs</td>
              </tr>
              <tr className="hover:bg-slate-100 dark:hover:bg-navy-dark/20 transition-colors">
                <td className="px-4 py-3 font-medium text-text-primary">Diego</td>
                <td className="px-4 py-3">Empate</td>
                <td className="px-4 py-3 text-center font-mono">1 - 1</td>
                <td className="px-4 py-3 text-right font-mono">50.00 Bs</td>
                <td className="px-4 py-3 text-center text-text-muted">No ❌</td>
                <td className="px-4 py-3 text-center font-mono">{diegoTickets}</td>
                <td className="px-4 py-3 text-right font-mono text-text-secondary">{diegoPremio.toFixed(2)} Bs</td>
              </tr>
              {/* Perdedores */}
              <tr className="opacity-50 hover:bg-slate-100 dark:hover:bg-navy-dark/20 transition-colors">
                <td className="px-4 py-3 text-text-muted">Elena</td>
                <td className="px-4 py-3">Argentina</td>
                <td className="px-4 py-3 text-center font-mono">2 - 0</td>
                <td className="px-4 py-3 text-right font-mono">80.00 Bs</td>
                <td className="px-4 py-3 text-center text-text-muted">—</td>
                <td className="px-4 py-3 text-center font-mono">0</td>
                <td className="px-4 py-3 text-right font-mono">0.00 Bs</td>
              </tr>
              <tr className="opacity-50 hover:bg-slate-100 dark:hover:bg-navy-dark/20 transition-colors">
                <td className="px-4 py-3 text-text-muted">Felipe</td>
                <td className="px-4 py-3">Argentina</td>
                <td className="px-4 py-3 text-center font-mono">2 - 1</td>
                <td className="px-4 py-3 text-right font-mono">40.00 Bs</td>
                <td className="px-4 py-3 text-center text-text-muted">—</td>
                <td className="px-4 py-3 text-center font-mono">0</td>
                <td className="px-4 py-3 text-right font-mono">0.00 Bs</td>
              </tr>
              <tr className="opacity-50 hover:bg-slate-100 dark:hover:bg-navy-dark/20 transition-colors">
                <td className="px-4 py-3 text-text-muted">Gisela</td>
                <td className="px-4 py-3">Francia</td>
                <td className="px-4 py-3 text-center font-mono">1 - 3</td>
                <td className="px-4 py-3 text-right font-mono">100.00 Bs</td>
                <td className="px-4 py-3 text-center text-text-muted">—</td>
                <td className="px-4 py-3 text-center font-mono">0</td>
                <td className="px-4 py-3 text-right font-mono">0.00 Bs</td>
              </tr>
              <tr className="opacity-50 hover:bg-slate-100 dark:hover:bg-navy-dark/20 transition-colors">
                <td className="px-4 py-3 text-text-muted">Héctor</td>
                <td className="px-4 py-3">Francia</td>
                <td className="px-4 py-3 text-center font-mono">0 - 2</td>
                <td className="px-4 py-3 text-right font-mono">50.00 Bs</td>
                <td className="px-4 py-3 text-center text-text-muted">—</td>
                <td className="px-4 py-3 text-center font-mono">0</td>
                <td className="px-4 py-3 text-right font-mono">0.00 Bs</td>
              </tr>
              <tr className="opacity-50 hover:bg-slate-100 dark:hover:bg-navy-dark/20 transition-colors">
                <td className="px-4 py-3 text-text-muted">Irene</td>
                <td className="px-4 py-3">Argentina</td>
                <td className="px-4 py-3 text-center font-mono">3 - 1</td>
                <td className="px-4 py-3 text-right font-mono">30.00 Bs</td>
                <td className="px-4 py-3 text-center text-text-muted">—</td>
                <td className="px-4 py-3 text-center font-mono">0</td>
                <td className="px-4 py-3 text-right font-mono">0.00 Bs</td>
              </tr>
              <tr className="opacity-50 hover:bg-slate-100 dark:hover:bg-navy-dark/20 transition-colors">
                <td className="px-4 py-3 text-text-muted">Juan</td>
                <td className="px-4 py-3">Francia</td>
                <td className="px-4 py-3 text-center font-mono">1 - 2</td>
                <td className="px-4 py-3 text-right font-mono">20.00 Bs</td>
                <td className="px-4 py-3 text-center text-text-muted">—</td>
                <td className="px-4 py-3 text-center font-mono">0</td>
                <td className="px-4 py-3 text-right font-mono">0.00 Bs</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Desglose Matemático */}
        <div className="space-y-4 pt-2 text-sm text-text-secondary leading-relaxed">
          <h4 className="font-display font-semibold text-text-primary">¿Cómo se calcula cada premio?</h4>
          <ol className="list-decimal list-inside space-y-2">
            <li>
              <strong className="font-semibold text-text-primary">Pozo Total Recaudado:</strong> Suma de todas las apuestas = <strong className="font-semibold text-text-primary">{totalRecaudado.toFixed(2)} Bs</strong>.
            </li>
            <li>
              <strong className="font-semibold text-text-primary">Pozo Neto a Distribuir:</strong> Descontando la comisión del {comision}% de la casa = <strong className="font-semibold text-text-primary">{pozoNeto.toFixed(2)} Bs</strong> ({totalRecaudado} Bs × {1 - comisionPct / 100}).
            </li>
            <li>
              <strong className="font-semibold text-text-primary">Tickets de Reparto Totales:</strong> Sumamos los tickets de los ganadores (quienes eligieron Empate):
              <div className="my-2 p-2.5 bg-slate-100 dark:bg-navy-dark/50 border border-border-subtle/40 rounded-lg font-mono text-xs sm:text-sm text-cyan text-center">
                Ana ({anaTickets}) + Bruno ({brunoTickets}) + Clara ({claraTickets}) + Diego ({diegoTickets}) = {totalTickets} tickets
              </div>
            </li>
            <li>
              <strong className="font-semibold text-text-primary">Valor de cada ticket:</strong> Dividimos el pozo neto entre la cantidad de tickets:
              <div className="my-2 p-2.5 bg-slate-100 dark:bg-navy-dark/50 border border-border-subtle/40 rounded-lg font-mono text-xs sm:text-sm text-cyan text-center">
                Valor del ticket = {pozoNeto.toFixed(2)} Bs / {totalTickets} = {valorTicket.toFixed(4)} Bs por ticket
              </div>
            </li>
            <li>
              <strong className="font-semibold text-text-primary">Premio en Bolivianos (Bs):</strong> Se multiplica el número de tickets de cada ganador por {valorTicket.toFixed(4)} Bs:
              <ul className="list-disc list-inside pl-4 space-y-1.5 mt-2 text-text-secondary">
                <li><span className="font-semibold text-text-primary">Ana:</span> {anaTickets} tickets × {valorTicket.toFixed(4)} Bs = <span className="font-bold text-success">{anaPremio.toFixed(2)} Bs</span> (Apostó 100 Bs)</li>
                <li><span className="font-semibold text-text-primary">Bruno:</span> {brunoTickets} tickets × {valorTicket.toFixed(4)} Bs = <span className="font-bold text-success">{brunoPremio.toFixed(2)} Bs</span> (Apostó 50 Bs)</li>
                <li><span className="font-semibold text-text-primary">Clara:</span> {claraTickets} tickets × {valorTicket.toFixed(4)} Bs = <span className="font-bold text-text-primary">{claraPremio.toFixed(2)} Bs</span> (Apostó 100 Bs)</li>
                <li><span className="font-semibold text-text-primary">Diego:</span> {diegoTickets} tickets × {valorTicket.toFixed(4)} Bs = <span className="font-bold text-text-primary">{diegoPremio.toFixed(2)} Bs</span> (Apostó 50 Bs)</li>
              </ul>
            </li>
          </ol>
          <div className="p-3 bg-cyan/5 border border-cyan/15 rounded-xl text-xs text-text-muted mt-2 space-y-2">
            <p>
              💡 <strong className="font-semibold text-text-primary">Conclusión:</strong> Quienes acertaron el marcador exacto (Ana y Bruno) multiplicaron su inversión por <strong className="font-semibold text-text-primary">{(anaPremio / 100).toFixed(2)} veces</strong>. Los perdedores perdieron sus apuestas de forma definitiva, financiando las ganancias.
            </p>
            <p className="text-text-secondary border-t border-border-subtle/30 pt-2">
              ⚠️ <strong className="font-semibold text-text-primary">Dividendo Mínimo Garantizado:</strong> Dado que el retorno obtenido por los ganadores básicos (Clara y Diego) es de <strong className="font-semibold text-text-primary">{(claraPremio / 100).toFixed(2)}x</strong> su apuesta, el cual es superior al dividendo mínimo configurado de <strong className="font-semibold text-text-primary">{dividendoMinimo}x</strong>, no se requiere aplicar una reducción de comisión ni inyectar un subsidio en este partido de ejemplo.
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}
